/**
 * Match Result Resolver - Golden Source of Truth for Point Distribution
 *
 * Shared by backend (standings) and frontend (UI) — the single source of truth.
 * This module provides a single, centralized function that determines point
 * distribution for all match types (standard 2v2 matches and bye matches).
 *
 * By centralizing this logic:
 * - Standings will always be accurate (they use this function)
 * - UI will always be consistent (it uses the same function)
 * - Point calculation is never duplicated or inconsistent
 * - New match types can be added easily
 */
/**
 * Points a player/pair receives for a bye (a "technical win"). This is the
 * single source of truth — standings, the resolver, and the UI all read it so
 * the displayed bye award can never drift from what standings actually grant.
 */
export const BYE_POINTS = 3;
/**
 * Resolve match points - THE GOLDEN SOURCE OF TRUTH
 *
 * This function is called by:
 * - Standings engine (to calculate points)
 * - UI component (to display points)
 * - Any other system that needs accurate point distribution
 *
 * CRITICAL: This function must be deterministic and consistent
 *
 * @param match The match object containing results
 * @returns MatchPoints object with teamAPoints, teamBPoints, and type
 */
export function resolveMatchPoints(match) {
    // Handle bye matches
    if (match.isBye === true || match.matchType === 'bye') {
        const byeTeamIndex = match.byePairIndex ?? 0;
        const teamAPoints = byeTeamIndex === 0 ? BYE_POINTS : 0;
        const teamBPoints = byeTeamIndex === 1 ? BYE_POINTS : 0;
        return {
            teamAPoints,
            teamBPoints,
            type: 'BYE',
            description: `Bye Match - Team ${byeTeamIndex === 0 ? 'A' : 'B'} gets ${BYE_POINTS} pts (technical win)`,
        };
    }
    // Handle pending matches (no score yet)
    if (match.status === 'pending' || !match.sets) {
        return {
            teamAPoints: 0,
            teamBPoints: 0,
            type: 'PENDING',
            description: 'Match not yet reported',
        };
    }
    // Parse sets if they're a string
    let setsArray = match.parsedSets || match.sets;
    if (typeof match.sets === 'string') {
        try {
            setsArray = JSON.parse(match.sets);
        }
        catch {
            return {
                teamAPoints: 0,
                teamBPoints: 0,
                type: 'PENDING',
                description: 'Invalid sets data',
            };
        }
    }
    // Validate sets array
    if (!Array.isArray(setsArray) || setsArray.length === 0) {
        return {
            teamAPoints: 0,
            teamBPoints: 0,
            type: 'PENDING',
            description: 'No sets played yet',
        };
    }
    // Count sets won by each team
    let teamASetsWon = 0;
    let teamBSetsWon = 0;
    for (const set of setsArray) {
        if (!set || typeof set.pair1score !== 'number' || typeof set.pair2score !== 'number') {
            continue; // Skip invalid sets
        }
        if (set.pair1score > set.pair2score) {
            teamASetsWon++;
        }
        else if (set.pair2score > set.pair1score) {
            teamBSetsWon++;
        }
        // Tied sets don't count in best-of-3
    }
    // Determine match type and award points
    // Best of 3 sets: first to 2 wins
    if (teamASetsWon === 2) {
        // Team A wins the match
        if (teamBSetsWon === 0) {
            // 2-0 victory
            return {
                teamAPoints: 3,
                teamBPoints: 0,
                type: '2-0',
                description: 'Team A wins 2-0 (3 pts) vs Team B (0 pts)',
            };
        }
        else if (teamBSetsWon === 1) {
            // 2-1 victory
            return {
                teamAPoints: 3,
                teamBPoints: 1,
                type: '2-1',
                description: 'Team A wins 2-1 (3 pts) vs Team B (1 pt)',
            };
        }
    }
    else if (teamBSetsWon === 2) {
        // Team B wins the match
        if (teamASetsWon === 0) {
            // 2-0 victory
            return {
                teamAPoints: 0,
                teamBPoints: 3,
                type: '2-0',
                description: 'Team B wins 2-0 (3 pts) vs Team A (0 pts)',
            };
        }
        else if (teamASetsWon === 1) {
            // 2-1 victory
            return {
                teamAPoints: 1,
                teamBPoints: 3,
                type: '2-1',
                description: 'Team B wins 2-1 (3 pts) vs Team A (1 pt)',
            };
        }
    }
    // Match not yet completed (e.g., 1-1 in sets)
    return {
        teamAPoints: 0,
        teamBPoints: 0,
        type: 'PENDING',
        description: 'Match in progress or incomplete',
    };
}
/**
 * Get a human-readable points display for a team
 *
 * Example usage in UI:
 * <span>{getPointsDisplay(matchPoints.teamAPoints)} pts</span>
 *
 * @param points The number of points
 * @returns Formatted string like "3 pts", "2 pts", etc.
 */
export function getPointsDisplay(points) {
    return `${points}`;
}
/**
 * Get CSS class for points display based on points awarded
 *
 * Example usage in UI:
 * <span class={getPointsClass(matchPoints.teamAPoints)}>...</span>
 *
 * @param points The number of points
 * @returns CSS class name
 */
export function getPointsClass(points) {
    if (points === 3)
        return 'font-black text-primary'; // Dominant win
    if (points === 2)
        return 'font-bold text-primary'; // Close win
    if (points === 1)
        return 'font-semibold text-accent'; // Close loss
    return 'text-slate-500 dark:text-slate-400'; // Loss
}
/**
 * Validate that a match has enough data to resolve points
 *
 * @param match The match to validate
 * @returns true if match can be resolved
 */
export function canResolveMatch(match) {
    // Bye matches can always be resolved
    if (match.isBye === true || match.matchType === 'bye') {
        return typeof match.byePairIndex === 'number';
    }
    // Regular matches need sets data
    if (!match.sets) {
        return false;
    }
    // Parse sets if string
    let setsArray = match.sets;
    if (typeof match.sets === 'string') {
        try {
            setsArray = JSON.parse(match.sets);
        }
        catch {
            return false;
        }
    }
    return Array.isArray(setsArray) && setsArray.length > 0;
}
//# sourceMappingURL=resultResolver.js.map